import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import {
  getUserScripts,
  getDeletedScripts,
  deleteScript,
  restoreScript,
  updateScript,
} from "../services/scriptService";
import { ScriptResponse } from "../types";
import TrialBanner from "../components/TrialBanner";
import ShotListPanel from "../components/ShotListPanel";
import VoiceoverPanel from "../components/VoiceoverPanel";
import ReferenceImagesPanel from "../components/ReferenceImagesPanel";
import ThumbnailsPanel from "../components/ThumbnailsPanel";
import SeriesStatusButton from "../components/SeriesStatusButton";

// Older scripts may contain "\n" as text instead of real line breaks
const cleanText = (text: string) => (text || "").replace(/\\n/g, "\n");

// PAGINATION: how many scripts to show per page
const PAGE_SIZE = 10;

const MyScripts = () => {
  const [scripts, setScripts] = useState<ScriptResponse[]>([]);
  const [deletedScripts, setDeletedScripts] = useState<ScriptResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ScriptResponse | null>(null);
  const [copied, setCopied] = useState(false);
  // PROMPT: "Copied!" message for the prompt's copy button
  const [promptCopied, setPromptCopied] = useState(false);
  const [page, setPage] = useState(1);

  // DELETE: tabs, confirmation pop-up and button states
  const [tab, setTab] = useState<"ACTIVE" | "DELETED">("ACTIVE");
  const [scriptToDelete, setScriptToDelete] = useState<ScriptResponse | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [actionError, setActionError] = useState("");

  // EDIT: editing the selected script
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");
  const [editNotice, setEditNotice] = useState("");
  const [editVersion, setEditVersion] = useState(0);

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchScripts = async () => {
      try {
        const [active, deleted] = await Promise.all([
          getUserScripts(),
          getDeletedScripts().catch(() => []),
        ]);
        setScripts(active);
        setDeletedScripts(deleted);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchScripts();
  }, []);

  // REMEMBER SCREEN: reopen the same script, page and tab after a refresh (this browser tab only)
  const myScriptsKey = user
    ? `tubescript-myscripts-${(user as any).email || (user as any).id || (user as any).fullName || "me"}`
    : "";
  const [screenRestored, setScreenRestored] = useState(false);

  // Bring back the saved screen once the scripts have loaded
  useEffect(() => {
    if (loading || screenRestored || !myScriptsKey) return;
    try {
      const raw = sessionStorage.getItem(myScriptsKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved.tab === "DELETED" || saved.tab === "ACTIVE")
          setTab(saved.tab);
        if (saved.page) setPage(saved.page);
        if (saved.selectedId) {
          const found = scripts.find((s) => s.id === saved.selectedId);
          if (found) setSelected(found);
        }
      }
    } catch {
      // If anything goes wrong, just show the normal page
    }
    setScreenRestored(true);
  }, [loading, screenRestored, myScriptsKey, scripts]);

  // Save the screen whenever it changes
  useEffect(() => {
    if (!screenRestored || !myScriptsKey) return;
    try {
      sessionStorage.setItem(
        myScriptsKey,
        JSON.stringify({ tab, page, selectedId: selected?.id ?? null }),
      );
    } catch {
      // Storage full or blocked: nothing to do
    }
  }, [screenRestored, myScriptsKey, tab, page, selected]);

  // Selecting a different script closes the editor
  const selectScript = (script: ScriptResponse | null) => {
    setSelected(script);
    setIsEditing(false);
    setEditError("");
    setEditNotice("");
  };

  // PAGINATION: work out which scripts to show on this page
  const totalPages = Math.max(1, Math.ceil(scripts.length / PAGE_SIZE));
  const pagedScripts = scripts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
    selectScript(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCopy = (script: ScriptResponse) => {
    const fullScript = `
TITLE: ${script.title}

SCRIPT:
${cleanText(script.scriptContent)}

DESCRIPTION:
${script.description}

HASHTAGS:
${script.hashtags}

THUMBNAIL IDEAS:
${script.thumbnailIdeas}
        `.trim();
    navigator.clipboard.writeText(fullScript);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // EDIT: open the editor with the current title and script
  const handleStartEdit = () => {
    if (!selected) return;
    setEditTitle(selected.title);
    setEditText(cleanText(selected.scriptContent));
    setEditError("");
    setEditNotice("");
    setIsEditing(true);
  };

  // EDIT: save the changes
  const handleSaveEdit = async () => {
    if (!selected) return;
    if (!editText.trim()) {
      setEditError("The script cannot be empty.");
      return;
    }
    setSavingEdit(true);
    setEditError("");
    try {
      const hadShotList = !!selected.shotList;
      const updated = await updateScript(selected.id, editText, editTitle);
      setScripts(scripts.map((s) => (s.id === updated.id ? updated : s)));
      setSelected(updated);
      setIsEditing(false);
      setEditVersion((v) => v + 1);
      setEditNotice(
        hadShotList && !updated.shotList
          ? "Your changes are saved. Your script changed, so please generate the shot list again to match it."
          : "Your changes are saved.",
      );
    } catch (err: any) {
      setEditError(
        err.response?.data?.message ||
          "Could not save your changes. Please try again.",
      );
    } finally {
      setSavingEdit(false);
    }
  };

  // DELETE: confirmed in the pop-up
  const handleConfirmDelete = async () => {
    if (!scriptToDelete) return;
    setDeleting(true);
    setActionError("");
    try {
      await deleteScript(scriptToDelete.id);
      const remaining = scripts.filter((s) => s.id !== scriptToDelete.id);
      setScripts(remaining);
      setDeletedScripts([scriptToDelete, ...deletedScripts]);
      if (selected?.id === scriptToDelete.id) selectScript(null);
      // Go back a page if this page is now empty
      const newTotalPages = Math.max(
        1,
        Math.ceil(remaining.length / PAGE_SIZE),
      );
      if (page > newTotalPages) setPage(newTotalPages);
      setScriptToDelete(null);
    } catch (err: any) {
      setActionError(
        err.response?.data?.message ||
          "Could not delete the script. Please try again.",
      );
    } finally {
      setDeleting(false);
    }
  };

  // DELETE: bring a script back from Recently Deleted
  const handleRestore = async (script: ScriptResponse) => {
    setRestoringId(script.id);
    setActionError("");
    try {
      const restored = await restoreScript(script.id);
      setDeletedScripts(deletedScripts.filter((s) => s.id !== script.id));
      setScripts(
        [restored, ...scripts].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ),
      );
    } catch (err: any) {
      setActionError(
        err.response?.data?.message ||
          "Could not restore the script. Please try again.",
      );
    } finally {
      setRestoringId(null);
    }
  };

  const isTrialExpired =
    user?.trialUsed &&
    !(user?.isPaid || (user as any)?.paid) &&
    user?.plan === "FREE";

  // Header buttons for the details panel, shared by mobile and desktop
  const renderDetailButtons = (isMobile: boolean) =>
    selected && !isEditing ? (
      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleStartEdit}
          className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded-lg text-xs transition"
        >
          Edit
        </button>
        <button
          onClick={() => handleCopy(selected)}
          className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded-lg text-xs transition"
        >
          {copied ? "Copied!" : "Copy All"}
        </button>
        <button
          onClick={() => setScriptToDelete(selected)}
          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-lg text-xs transition"
        >
          Delete
        </button>
        {isMobile && (
          <button
            onClick={() => selectScript(null)}
            className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded-lg text-xs transition"
          >
            ✕ Close
          </button>
        )}
      </div>
    ) : null;

  // Details panel content, shared by mobile and desktop
  const renderDetails = (script: ScriptResponse, isMobile: boolean) => (
    <>
      {editNotice && !isEditing && (
        <div className="bg-green-900 border border-green-700 text-green-300 px-4 py-3 rounded-lg text-sm">
          {editNotice}
        </div>
      )}

      <div className="bg-gray-900 rounded-xl p-4">
        <h3 className="text-gray-400 text-xs uppercase mb-2">Title</h3>
        {isEditing ? (
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            maxLength={255}
            className="w-full bg-gray-800 border border-gray-600 text-white px-3 py-2 rounded-lg focus:outline-none focus:border-purple-500 transition"
          />
        ) : (
          <p
            className={`text-white font-semibold ${isMobile ? "text-sm" : ""}`}
          >
            {script.title}
          </p>
        )}
      </div>

      {/* PROMPT: the prompt this script was generated from, with a copy button */}
      {!isEditing && script.topic && (
        <div className="bg-gray-900 rounded-xl p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="text-gray-400 text-xs uppercase">Prompt</h3>
            <button
              onClick={() => {
                navigator.clipboard.writeText(script.topic);
                setPromptCopied(true);
                setTimeout(() => setPromptCopied(false), 2000);
              }}
              className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded-lg text-xs transition"
            >
              {promptCopied ? "Copied!" : "Copy prompt"}
            </button>
          </div>
          <p className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">
            {script.topic}
          </p>
        </div>
      )}

      <div
        className={`bg-gray-900 rounded-xl p-4 ${isEditing ? "" : "max-h-60 overflow-y-auto"}`}
      >
        <h3 className="text-gray-400 text-xs uppercase mb-2">Script</h3>
        {isEditing ? (
          <>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={18}
              className="w-full bg-gray-800 border border-gray-600 text-white p-3 rounded-lg focus:outline-none focus:border-purple-500 transition resize-y text-sm leading-relaxed"
            />
            {editError && (
              <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-2 rounded-lg text-sm mt-3">
                {editError}
              </div>
            )}
            <div className="flex gap-3 mt-3">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditError("");
                }}
                disabled={savingEdit}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-xl text-sm transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900 text-white py-3 rounded-xl font-semibold text-sm transition"
              >
                {savingEdit ? "Saving..." : "Save changes"}
              </button>
            </div>
          </>
        ) : (
          <p className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">
            {cleanText(script.scriptContent)}
          </p>
        )}
      </div>

      <div className="bg-gray-900 rounded-xl p-4">
        <h3 className="text-gray-400 text-xs uppercase mb-2">Description</h3>
        <p className="text-gray-300 text-sm">{script.description}</p>
      </div>

      <div className="bg-gray-900 rounded-xl p-4">
        <h3 className="text-gray-400 text-xs uppercase mb-2">#️Hashtags</h3>
        <p className="text-purple-400 text-sm">{script.hashtags}</p>
      </div>

      <div className="bg-gray-900 rounded-xl p-4">
        <h3 className="text-gray-400 text-xs uppercase mb-2">
          Thumbnail Ideas
        </h3>
        <p className="text-gray-300 text-sm">{script.thumbnailIdeas}</p>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar />
      <TrialBanner />

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">My Scripts</h1>
            <p className="text-gray-400 mt-1 text-sm md:text-base">
              All your generated scripts in one place
            </p>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-semibold transition"
          >
            Generate New Script
          </button>
        </div>

        {/* DELETE: tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => {
              setTab("ACTIVE");
              selectScript(null);
            }}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              tab === "ACTIVE"
                ? "bg-purple-600 text-white font-semibold"
                : "bg-gray-800 text-gray-400 hover:bg-gray-700"
            }`}
          >
            My Scripts ({scripts.length})
          </button>
          <button
            onClick={() => {
              setTab("DELETED");
              selectScript(null);
            }}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              tab === "DELETED"
                ? "bg-purple-600 text-white font-semibold"
                : "bg-gray-800 text-gray-400 hover:bg-gray-700"
            }`}
          >
            Recently Deleted ({deletedScripts.length})
          </button>
        </div>

        {actionError && (
          <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm mb-6">
            {actionError}
          </div>
        )}

        {/* Trial Expired Banner */}
        {isTrialExpired && tab === "ACTIVE" && (
          <div className="bg-red-900 border border-red-700 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-red-300 font-semibold text-sm">
                ⚠️ Your 2 trial scripts are hidden!
              </p>
              <p className="text-red-400 text-xs mt-1">
                Upgrade to PRO or Agency to access your trial scripts again.
              </p>
            </div>
            <button
              onClick={() => navigate("/pricing")}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm transition w-full sm:w-auto"
            >
              Upgrade Now
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-gray-400">Loading your scripts...</div>
          </div>
        ) : tab === "DELETED" ? (
          /* DELETE: Recently Deleted tab */
          deletedScripts.length === 0 ? (
            <div className="text-center py-20 px-4 text-gray-400">
              <p className="text-lg font-semibold text-white mb-2">
                Nothing here
              </p>
              <p className="text-sm">
                Scripts you delete will appear here, so you can restore them.
              </p>
            </div>
          ) : (
            <div className="space-y-4 max-w-3xl">
              {deletedScripts.map((script) => (
                <div
                  key={script.id}
                  className="bg-gray-800 rounded-xl p-4 md:p-5 border border-gray-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white mb-1 text-sm md:text-base truncate">
                      {script.title}
                    </h3>
                    <p className="text-gray-400 text-xs md:text-sm truncate">
                      {script.topic} • {script.niche}
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      Created {new Date(script.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRestore(script)}
                    disabled={restoringId === script.id}
                    className="w-full sm:w-auto bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold transition"
                  >
                    {restoringId === script.id ? "Restoring..." : "Restore"}
                  </button>
                </div>
              ))}
            </div>
          )
        ) : scripts.length === 0 ? (
          <div className="text-center py-20 px-4">
            <h2 className="text-xl md:text-2xl font-bold mb-2">
              {isTrialExpired ? "No free plan scripts yet!" : "No scripts yet!"}
            </h2>
            <p className="text-gray-400 mb-6 text-sm md:text-base">
              {isTrialExpired
                ? "Your trial scripts are hidden. Generate new scripts on your free plan!"
                : "Generate your first script to see it here"}
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-xl font-semibold transition"
            >
              Generate Script
            </button>
          </div>
        ) : (
          <>
            {/* Mobile: show detail above list when selected */}
            {selected && (
              <div className="md:hidden bg-gray-800 rounded-xl border border-purple-500 p-4 mb-6 space-y-4">
                <div className="flex flex-col gap-2">
                  <h2 className="font-bold text-purple-400">
                    {isEditing ? "Editing Script" : "Script Details"}
                  </h2>
                  {renderDetailButtons(true)}
                </div>

                {renderDetails(selected, true)}

                {/* VOICEOVER and SHOT LIST (mobile) */}
                {!isEditing && (
                  <>
                    <ThumbnailsPanel
                      key={`mobile-thumbs-${selected.id}`}
                      script={selected}
                    />
                    <SeriesStatusButton
                      key={`mobile-series-${selected.id}`}
                      script={selected}
                    />
                    <VoiceoverPanel
                      key={`mobile-voice-${selected.id}-${editVersion}`}
                      script={selected}
                    />

                    <ReferenceImagesPanel
                      key={`mobile-refs-${selected.id}-${editVersion}`}
                      script={selected}
                    />

                    <ShotListPanel
                      key={`mobile-${selected.id}-${editVersion}`}
                      script={selected}
                    />
                  </>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Scripts List */}
              <div className="space-y-4">
                {pagedScripts.map((script) => (
                  <div
                    key={script.id}
                    onClick={() => selectScript(script)}
                    className={`bg-gray-800 rounded-xl p-4 md:p-5 border cursor-pointer transition ${
                      selected?.id === script.id
                        ? "border-purple-500"
                        : "border-gray-700 hover:border-purple-700"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white mb-1 text-sm md:text-base truncate">
                          {script.title}
                        </h3>
                        <p className="text-gray-400 text-xs md:text-sm truncate">
                          {script.topic} • {script.niche}
                        </p>
                        <p className="text-gray-500 text-xs mt-1">
                          {new Date(script.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      {/* DELETE: button on each script in the list */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setScriptToDelete(script);
                        }}
                        className="bg-gray-700 hover:bg-red-600 text-white px-3 py-1 rounded-lg text-xs transition flex-shrink-0"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}

                {/* PAGINATION controls */}
                {totalPages > 1 && (
                  <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                    <button
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                      className="bg-gray-800 border border-gray-700 hover:border-purple-600 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg text-sm transition"
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (n) => (
                        <button
                          key={n}
                          onClick={() => goToPage(n)}
                          className={`px-3 py-2 rounded-lg text-sm transition ${
                            n === page
                              ? "bg-purple-600 text-white font-semibold"
                              : "bg-gray-800 border border-gray-700 hover:border-purple-600 text-gray-300"
                          }`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                    <button
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                      className="bg-gray-800 border border-gray-700 hover:border-purple-600 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg text-sm transition"
                    >
                      Next
                    </button>
                  </div>
                )}
                {totalPages > 1 && (
                  <p className="text-center text-gray-500 text-xs">
                    Page {page} of {totalPages} · {scripts.length} scripts
                  </p>
                )}
              </div>

              {/* Script Detail - Desktop only */}
              <div className="hidden md:block bg-gray-800 rounded-xl border border-gray-700 p-6 h-fit sticky top-6">
                {selected ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <h2 className="font-bold text-purple-400">
                        {isEditing ? "Editing Script" : "Script Details"}
                      </h2>
                      {renderDetailButtons(false)}
                    </div>

                    {renderDetails(selected, false)}

                    {/* VOICEOVER and SHOT LIST (desktop) */}
                    {!isEditing && (
                      <>
                        <ThumbnailsPanel
                          key={`desktop-thumbs-${selected.id}`}
                          script={selected}
                        />
                        <SeriesStatusButton
                          key={`desktop-series-${selected.id}`}
                          script={selected}
                        />
                        <VoiceoverPanel
                          key={`desktop-voice-${selected.id}-${editVersion}`}
                          script={selected}
                        />

                        <ReferenceImagesPanel
                          key={`desktop-refs-${selected.id}-${editVersion}`}
                          script={selected}
                        />

                        <div className="max-h-[28rem] overflow-y-auto">
                          <ShotListPanel
                            key={`desktop-${selected.id}-${editVersion}`}
                            script={selected}
                          />
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-10 text-gray-400">
                    <p className="text-4xl mb-3">⬅</p>
                    <p>Click a script to view details</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* DELETE: "Are you sure?" pop-up */}
      {scriptToDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-md text-white">
            <h3 className="text-xl font-bold mb-2">Delete this script?</h3>
            <p className="text-gray-300 text-sm mb-1">
              Are you sure you want to delete{" "}
              <strong className="text-white">{scriptToDelete.title}</strong>?
            </p>
            <p className="text-gray-400 text-xs mb-5">
              You can restore it later from Recently Deleted.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setScriptToDelete(null)}
                disabled={deleting}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-xl text-sm transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-3 rounded-xl font-semibold text-sm transition"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyScripts;
